import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_SIZE = 34;

export const formatUtcDateKey = (date: Date): string => {
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = date.getUTCFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

export const parseUtcDateKey = (value: string): Date | null => {
  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[3]), Number(match[2]) - 1, Number(match[1])));
  return Number.isNaN(date.getTime()) ? null : date;
};

const utcToday = (): Date => {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
};

const monthStart = (date: Date): Date => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));

interface QueueDateCalendarProps {
  /** Selected day as DD-MM-YYYY (UTC). */
  value: string;
  /** Days that have queue messages, as DD-MM-YYYY. Every other day is disabled. */
  availableDates: ReadonlySet<string>;
  onChange: (dateKey: string) => void;
  /** Inclusive UTC window ending today. Days outside it stay disabled. */
  dayWindow?: number;
  /** While true, every day stays disabled until the available set is known. */
  loading?: boolean;
}

export const QueueDateCalendar: React.FC<QueueDateCalendarProps> = ({
  value,
  availableDates,
  onChange,
  dayWindow = 90,
  loading = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selected = parseUtcDateKey(value);
  const [visibleMonth, setVisibleMonth] = useState<Date>(() => monthStart(selected ?? utcToday()));

  useEffect(() => {
    if (!anchorEl) return;
    const parsed = parseUtcDateKey(value);
    if (parsed) setVisibleMonth(monthStart(parsed));
  }, [anchorEl, value]);

  const today = utcToday();
  const todayKey = formatUtcDateKey(today);
  const minDate = useMemo(() => {
    const start = new Date(today.getTime());
    start.setUTCDate(start.getUTCDate() - (Math.max(dayWindow, 1) - 1));
    return start;
  }, [todayKey, dayWindow]);
  const minMonthTime = monthStart(minDate).getTime();
  const maxMonthTime = monthStart(today).getTime();
  const visibleMonthTime = visibleMonth.getTime();

  const cells = useMemo(() => {
    const year = visibleMonth.getUTCFullYear();
    const month = visibleMonth.getUTCMonth();
    const firstWeekday = (new Date(Date.UTC(year, month, 1)).getUTCDay() + 6) % 7;
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const slots: Array<Date | null> = [];
    for (let i = 0; i < firstWeekday; i += 1) slots.push(null);
    for (let day = 1; day <= daysInMonth; day += 1) slots.push(new Date(Date.UTC(year, month, day)));
    while (slots.length % 7 !== 0) slots.push(null);
    return slots;
  }, [visibleMonth]);

  const monthLabel = visibleMonth.toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  const shiftMonth = (delta: number) => {
    setVisibleMonth((current) => new Date(Date.UTC(current.getUTCFullYear(), current.getUTCMonth() + delta, 1)));
  };

  return (
    <>
      <Button
        variant="outlined"
        color="secondary"
        size="small"
        startIcon={<CalendarMonthIcon sx={{ fontSize: 18 }} />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-label={`Queue date ${value}. Open calendar`}
        sx={{
          minWidth: { xs: '100%', sm: 180 },
          height: 37,
          px: 1.25,
          justifyContent: 'flex-start',
          textTransform: 'none',
          fontWeight: 500,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 0.5, p: 1.5, width: 292 } } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <IconButton size="small" aria-label="Previous month" onClick={() => shiftMonth(-1)} disabled={visibleMonthTime <= minMonthTime}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{monthLabel}</Typography>
          <IconButton size="small" aria-label="Next month" onClick={() => shiftMonth(1)} disabled={visibleMonthTime >= maxMonthTime}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
          {WEEKDAYS.map((day) => (
            <Typography
              key={day}
              variant="caption"
              color="text.secondary"
              sx={{ textAlign: 'center', fontWeight: 700, lineHeight: '28px' }}
            >
              {day}
            </Typography>
          ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.25, justifyItems: 'center' }}>
          {cells.map((date, index) => {
            if (!date) return <Box key={`empty-${index}`} sx={{ width: DAY_SIZE, height: DAY_SIZE }} />;
            const key = formatUtcDateKey(date);
            const inWindow = date.getTime() >= minDate.getTime() && date.getTime() <= today.getTime();
            const enabled = !loading && inWindow && availableDates.has(key);
            const isSelected = key === value;
            const isToday = key === todayKey;
            const button = (
              <Button
                key={key}
                disabled={!enabled}
                onClick={() => {
                  onChange(key);
                  setAnchorEl(null);
                }}
                aria-label={enabled ? key : `${key}, no messages`}
                aria-pressed={isSelected}
                sx={{
                  width: DAY_SIZE,
                  height: DAY_SIZE,
                  minWidth: DAY_SIZE,
                  maxWidth: DAY_SIZE,
                  p: 0,
                  flex: '0 0 auto',
                  aspectRatio: '1 / 1',
                  borderRadius: '50%',
                  boxShadow: 'none',
                  lineHeight: 1,
                  fontSize: '0.8rem',
                  fontWeight: isSelected || isToday ? 700 : 500,
                  color: isSelected ? 'secondary.contrastText' : 'text.primary',
                  backgroundColor: isSelected ? 'secondary.main' : 'transparent',
                  border: isToday && !isSelected ? '1px solid' : '1px solid transparent',
                  borderColor: isToday && !isSelected ? 'secondary.main' : 'transparent',
                  '&:hover': {
                    backgroundColor: isSelected ? 'secondary.dark' : 'action.hover',
                    boxShadow: 'none',
                  },
                  '&.Mui-disabled': {
                    width: DAY_SIZE,
                    height: DAY_SIZE,
                    minWidth: DAY_SIZE,
                    maxWidth: DAY_SIZE,
                    p: 0,
                    opacity: 1,
                    borderRadius: '50%',
                    boxShadow: 'none',
                    backgroundColor: 'action.hover',
                    color: 'text.disabled',
                    borderColor: 'transparent',
                  },
                }}
              >
                {date.getUTCDate()}
              </Button>
            );
            if (enabled) return button;
            return (
              <Tooltip key={key} title={loading ? 'Loading dates' : 'No messages on this date'}>
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: DAY_SIZE, height: DAY_SIZE }}>{button}</Box>
              </Tooltip>
            );
          })}
        </Box>
      </Popover>
    </>
  );
};
